import type { ComposeBodyDocument, ComposeOutboundBody } from './types';
import { plainTextToPreHtml } from './plainTextToHtml';
import { normalizePlainText } from './normalizePlainText';
import { extractPlainTextFromHtml, plainBodyFromParts } from './htmlToPlainText';
import { resolveQuotedBodyHtmlForSend } from './quotedBodyDisplay';
import { quotedHeaderToOutboundHtml } from './quotedHeaderMeta';
import { normalizeQuotedBlock } from './quotedBlockNormalize';

export type ComposeDocToOutboundOptions = {
  /** Default true — set false when saving drafts/templates (signature is added at send time). */
  includeSignature?: boolean;
  /** Default true — set false when saving library templates (quoted original stays out of template body). */
  includeQuoted?: boolean;
};

/** Compose document without the account signature block (for draft/template persistence). */
export function composeDocWithoutSignature(doc: ComposeBodyDocument): ComposeBodyDocument {
  if (!doc.signature) return doc;
  return { ...doc, signature: undefined };
}

function quotedToText(quoted: NonNullable<ComposeBodyDocument['quoted']>): string {
  const header = quoted.headerLines.join('\n');
  const body = quoted.bodyText?.trim()
    ? quoted.bodyText
    : quoted.bodyHtml
      ? plainBodyFromParts(quoted.bodyHtml, undefined)
      : '';
  return body ? `${header}\n\n${body}` : header;
}

function quotedToHtml(quoted: NonNullable<ComposeBodyDocument['quoted']>): string {
  const normalized = normalizeQuotedBlock(quoted);
  const headerHtml = quotedHeaderToOutboundHtml(normalized.headerLines);
  const bodyHtml = resolveQuotedBodyHtmlForSend(normalized);
  return `<blockquote class="email-quoted-block">${headerHtml}${bodyHtml}</blockquote>`;
}

export function composeDocToOutbound(
  doc: ComposeBodyDocument,
  options?: ComposeDocToOutboundOptions,
): ComposeOutboundBody {
  const includeSignature = options?.includeSignature !== false;
  const includeQuoted = options?.includeQuoted !== false;
  const sigHtml = doc.signature?.trim() ?? '';

  let userText = '';
  let userHtml = '';

  if (doc.bodyKind === 'plain') {
    userText = normalizePlainText(doc.userBody);
    userHtml = plainTextToPreHtml(userText);
  } else {
    userHtml = doc.userBody;
    // Preserve Enter/returns from <p>/<div>/<br> — never use textContent (collapses blocks).
    // Do not reflow FM:/TO: ops soft-wraps here — that joins intentional Enter lines.
    userText = extractPlainTextFromHtml(userHtml, { reflowOpsTemplates: false });
  }

  const partsText: string[] = [];
  const partsHtml: string[] = [];

  if (userText.trim()) partsText.push(userText);
  if (userHtml.trim()) partsHtml.push(userHtml);

  if (includeSignature && sigHtml) {
    // Signature is HTML — extract plain text with Enter/returns preserved (do not push raw HTML).
    const signaturePlain = extractPlainTextFromHtml(sigHtml, { reflowOpsTemplates: false });
    if (signaturePlain.trim()) partsText.push(signaturePlain);
    partsHtml.push(sigHtml);
  }

  if (includeQuoted && doc.quoted) {
    partsText.push(quotedToText(doc.quoted));
    partsHtml.push(quotedToHtml(doc.quoted));
  }

  return {
    text: partsText.filter(Boolean).join('\n\n'),
    html: partsHtml.filter(Boolean).join('\n\n'),
  };
}

/** Derive legacy formData.html from compose doc (for preview / transitional sync). */
export function composeDocToEditorHtml(doc: ComposeBodyDocument): string {
  return composeDocToOutbound(doc).html;
}
